'use client';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { RightModule } from '@/components/right-wrapper/right-wrapper.config';
import { getApiV1AuthAccess } from '@/query/auth';
import type { TPermissions } from '@/types';
import { ModuleTag } from '@/types';
import { checkAccess, checkRightsAccess as checkRightsAccessUtil, getModuleAccess, getRightModuleAccess } from '@/utils/permissions';

export const useAccessData = <M extends keyof TPermissions>(moduleTag?: M, moduleView?: keyof TPermissions[M]) => {
  const { data, isLoading, isError, error } = useQuery(getApiV1AuthAccess);

  const checkModuleAccess = useCallback(<T extends keyof TPermissions>(moduleName: T, viewIndex?: keyof TPermissions[T]): boolean => {
    if (!data) {
      return false;
    }
    const moduleInfo = getModuleAccess(data, moduleName);
    if (!moduleInfo) {
      return false;
    }

    return checkAccess(moduleInfo, moduleName, viewIndex);
  }, [data]);

  const checkRightsAccess = useCallback((moduleName: RightModule): boolean => {
    if (!data) {
      return false;
    }

    const moduleInfo = getRightModuleAccess(data, moduleName);
    if (!moduleInfo) {
      return false;
    }

    return checkRightsAccessUtil(moduleInfo, moduleName);
  }, [data]);

  const hasAccess: boolean = useMemo(() => {
    if (moduleTag === undefined) {
      return true;
    }
    return checkModuleAccess(moduleTag, moduleView);
  }, [data]);

  const isSuperUser = checkModuleAccess(ModuleTag.SUPER_USER);

  return {
    accessData: data,
    hasAccess,
    checkModuleAccess,
    checkRightsAccess,
    isLoading,
    isError,
    error,
    isSuperUser,
  };
};
